import React, { useEffect, useState } from 'react';
import axios from "axios";
import { format } from "date-fns";
import InterviewScheduler from "../candidate/InterviewScheduler";
import EmployerDashboardNav from "../../employer/EmployerDashboardNav";
import Breadcrumb from '../../Breadcrumb';

const PostedInterviews = () => {
  const [interviews, setInterviews] = useState([]);
  const [interviewPerPage] = useState(5);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [modalStatus, setModalStatus] = useState('confirm');
  const [modalMessage, setModalMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [scheduleModal, setScheduleModal] = useState(false);
  const companyId = localStorage.getItem('companyId');

  const filteredInterviews = interviews
    .filter(interview =>
      (filterStatus === 'all' || interview.status === filterStatus) &&
      (interview.interviewId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (interview.name && interview.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (interview.job_name && interview.job_name.toLowerCase().includes(searchTerm.toLowerCase())))
    );

  const indexOfLastInterview = currentPage * interviewPerPage;
  const indexOfFirstInterview = indexOfLastInterview - interviewPerPage;
  const currentInterviews = filteredInterviews.slice(indexOfFirstInterview, indexOfLastInterview);
  const totalPages = Math.ceil(filteredInterviews.length / interviewPerPage);

  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch (error) {
      return dateString;
    }
  };

  const formatTime = (timeString) => {
    try {
      const [hours, minutes] = timeString.split(':');
      return `${hours % 12 || 12}:${minutes} ${hours >= 12 ? 'PM' : 'AM'}`;
    } catch (error) {
      return timeString;
    }
  };

  const fetchInterviews = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}interviews/fetch-interviews-by-company/${companyId}`);
      setInterviews(response.data);
    } catch (error) {
      console.error("Error fetching interviews:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInterviews();
  }, []);

  const paginate = (pageNumber) => {
    if (pageNumber < 1 || pageNumber > totalPages) return;
    setCurrentPage(pageNumber);

    document.querySelector('.table-container').scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleViewInterview = (interview) => {
    setSelectedInterview(interview);
    setModalType('view');
    setShowModal(true);
  };

  const handleCancelInterview = (interview) => {
    setSelectedInterview(interview);
    setModalType('cancel');
    setModalStatus('before-delete');
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setTimeout(() => {
      setModalStatus('confirm');
      setSelectedInterview(null);
      setModalMessage('');
    }, 300);
  };

  const processAction = async () => {
    setIsProcessing(true);
    setModalStatus('processing');

    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}interviews/cancel/${selectedInterview.interviewId}`);

      if (response.status === 200) {
        setModalMessage('Interview has been successfully cancelled.');
        setModalStatus('success');
      } else {
        setModalMessage("Error Cancelling Interview");
        setModalStatus('error');
      }
    } catch (error) {
      console.error("Error cancelling interview:", error);
      setModalMessage("Error Cancelling Interview");
      setModalStatus('error');
    } finally {
      setIsProcessing(false);
      setTimeout(handleModalClose, 2000);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'scheduled':
        return 'bg-warning';
      case 'accepted':
        return 'bg-success';
      case 'completed':
        return 'bg-info';
      case 'canceled':
        return 'bg-danger';
      case 'rejected':
        return 'bg-danger';
      default:
        return 'bg-secondary';
    }
  };

  const renderModalContent = () => {
    switch (modalStatus) {
      case 'processing':
        return (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Processing...</span>
            </div>
            <p className="mt-3">Processing your request...</p>
          </div>
        );

      case 'success':
        return (
          <div className="text-center text-success py-4">
            <i className="fas fa-check-circle fa-3x mb-3"></i>
            <p>{modalMessage}</p>
          </div>
        );

      case 'error':
        return (
          <div className="text-center text-danger py-4">
            <i className="fas fa-exclamation-circle fa-3x mb-3"></i>
            <p>{modalMessage}</p>
          </div>
        );

      case "before-delete":
        return (
          <div className="text-center text-danger py-4">
            <i className="fas fa-trash-alt fa-3x mb-3"></i>
            <p className="mb-0">
              Are you sure you want to cancel this interview?
            </p>
            <p>Interview ID: {selectedInterview?.interviewId}</p>
            <p className="fw-bold mt-2">Candidate: {selectedInterview?.name}</p>
            <p>Job: {selectedInterview?.job_name}</p>
          </div>
        );

      default:
        if (modalType === 'view') {
          return (
            <div className="interview-details p-2">
              <div className="row mb-3">
                <div className="col-md-6">
                  <p className="mb-1"><strong>Interview ID:</strong></p>
                  <p>{selectedInterview?.interviewId}</p>
                </div>
                <div className="col-md-6">
                  <p className="mb-1"><strong>Status:</strong></p>
                  <p>
                    <span className={`badge ${getStatusBadgeClass(selectedInterview?.status)}`}>
                      {selectedInterview?.status}
                    </span>
                  </p>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <p className="mb-1"><strong>Candidate:</strong></p>
                  <p>{selectedInterview?.name}</p>
                </div>
                <div className="col-md-6">
                  <p className="mb-1"><strong>Position:</strong></p>
                  <p>{selectedInterview?.job_name}</p>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <p className="mb-1"><strong>Date:</strong></p>
                  <p>{formatDate(selectedInterview?.date)}</p>
                </div>
                <div className="col-md-6">
                  <p className="mb-1"><strong>Time:</strong></p>
                  <p>{formatTime(selectedInterview?.time)}</p>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-12">
                  <p className="mb-1"><strong>Location:</strong></p>
                  <p>{selectedInterview?.location}</p>
                </div>
              </div>

              {selectedInterview?.meetingLink && (
                <div className="row mb-3">
                  <div className="col-12">
                    <p className="mb-1"><strong>Meeting Link:</strong></p>
                    <p>
                      <a href={selectedInterview.meetingLink} target="_blank" rel="noreferrer">
                        {selectedInterview.meetingLink}
                      </a>
                    </p>
                  </div>
                </div>
              )}

              {selectedInterview?.additionalNotes && (
                <div className="row mb-3">
                  <div className="col-12">
                    <p className="mb-1"><strong>Additional Notes:</strong></p>
                    <p>{selectedInterview.additionalNotes}</p>
                  </div>
                </div>
              )}
            </div>
          );
        }

        return null;
    }
  };

  return (
    <>
      <div className="col-lg fade-in">
        <div className="card border-1 shadow-sm">
          <div className="card-header bg-white d-flex justify-content-between align-items-center py-3">
            <h5 className="mb-0 section-title">
              <i className="fas fa-calendar-check text-primary me-2"></i>
              Interview Management
            </h5>
            <button className="btn btn-primary" onClick={() => fetchInterviews()}>
              <i className="fas fa-refresh me-2"></i>Refresh
            </button>
          </div>

          <div className="card-body">
            <div className="row mb-4">
              <div className="col-md-6">
                <div className="input-group">
                  <span className="input-group-text bg-light border-end-0">
                    <i className="fas fa-search"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control border-start-0 ps-0 custom-input p-2"
                    placeholder="Search by ID, candidate or job title..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </div>
              </div>
              <div className="col-md-6">
                <select
                  className="form-select"
                  value={filterStatus}
                  onChange={(e) => {
                    setFilterStatus(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="all">All Statuses</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="accepted">Accepted</option>
                  <option value="completed">Completed</option>
                  <option value="canceled">Canceled</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
            </div>

            <div className="table-responsive">
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading interviews...</span>
                  </div>
                  <p className="mt-3">Loading interviews...</p>
                </div>
              ) : interviews.length > 0 ? (
                <div className="table-container">
                  <table className="table table-hover align-middle border-bottom animate__animated animate__fadeIn">
                    <thead className="bg-light">
                    <tr>
                      <th className="fw-medium">Candidate Name</th>
                      <th className="fw-medium">Position</th>
                      <th className="fw-medium">Date & Time</th>
                      <th className="fw-medium">Location</th>
                      <th className="fw-medium">Status</th>
                      <th className="fw-medium text-center" style={{ width: "220px" }}>Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    {currentInterviews.map((interview) => (
                      <tr key={interview.interviewId} className="interview-row animate__animated animate__fadeIn">
                        <td>
                          <div className="d-flex align-items-center">
                            <div>
                              <p className="mb-0 fw-medium">{interview.name}</p>
                              <small className="text-muted">{interview.interviewId}</small>
                            </div>
                          </div>
                        </td>
                        <td>{interview.job_name}</td>
                        <td>
                          <div>
                            <div className="mb-1">
                              <i className="far fa-calendar-alt me-2 text-muted"></i>
                              {formatDate(interview.date)}
                            </div>
                            <div>
                              <i className="far fa-clock me-2 text-muted"></i>
                              {formatTime(interview.time)}
                            </div>
                          </div>
                        </td>
                        <td>
                          {interview.location.toLowerCase() === "remote" ? (
                            <span className="text-primary">
                                <i className="fas fa-video me-2"></i>Remote
                              </span>
                          ) : (
                            <span>
                                <i className="fas fa-building me-2"></i>{interview.location}
                              </span>
                          )}
                        </td>
                        <td>
                            <span className={`badge ${getStatusBadgeClass(interview.status)}`}>
                              {interview.status}
                            </span>
                        </td>
                        <td>
                          <div className="d-flex justify-content-center" style={{ minWidth: "200px" }}>
                            <button
                              onClick={() => handleViewInterview(interview)}
                              className="btn btn-sm btn-outline-primary me-2"
                              title="View Details"
                            >
                              <i className="fas fa-eye"></i> Details
                            </button>
                            <button
                              onClick={() => {
                                setScheduleModal(true);
                                setSelectedInterview(interview);
                              }}
                              className="btn btn-sm btn-outline-primary me-2"
                              title="Reschedule Interview"
                            >
                              <i className="fas fa-calendar"></i> Reschedule
                            </button>
                            <button
                              onClick={() => handleCancelInterview(interview)}
                              className="btn btn-sm btn-outline-danger"
                              title="Cancel Interview"
                              disabled={interview.status === "canceled"}
                            >
                              <i className="fas fa-times"></i> Cancel
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    </tbody>
                  </table>

                  {currentInterviews.length === 0 && (
                    <div className="text-center py-5 animate__animated animate__fadeIn">
                      <i className="fas fa-search fa-3x text-muted mb-3"></i>
                      <h5>No interviews found</h5>
                      <p className="text-muted">Try adjusting your search or filter criteria</p>
                    </div>
                  )}

                  {filteredInterviews.length > 0 && (
                    <div className="d-flex justify-content-between align-items-center mt-4">
                      <div className="entries-info">
                        Showing {indexOfFirstInterview + 1} to {Math.min(indexOfLastInterview, filteredInterviews.length)} of {filteredInterviews.length} interviews
                      </div>

                      <nav aria-label="Page navigation">
                        <ul className="pagination mb-0">
                          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => paginate(currentPage - 1)}
                              aria-label="Previous"
                            >
                              <span aria-hidden="true">&laquo;</span>
                            </button>
                          </li>

                          {pageNumbers.map(number => (
                            <li
                              key={number}
                              className={`page-item ${currentPage === number ? 'active' : ''}`}
                            >
                              <button
                                className="page-link"
                                onClick={() => paginate(number)}
                              >
                                {number}
                              </button>
                            </li>
                          ))}

                          <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => paginate(currentPage + 1)}
                              aria-label="Next"
                            >
                              <span aria-hidden="true">&raquo;</span>
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-5 animate__animated fade-in">
                  <i className="far fa-calendar-check fa-4x text-muted mb-3"></i>
                  <h4>No interviews available</h4>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className={`modal fade ${showModal ? 'show' : ''}`}
           id="interviewActionModal"
           tabIndex="-1"
           style={{ display: showModal ? 'block' : 'none' }}
           aria-labelledby="interviewActionModalLabel"
           aria-hidden={!showModal}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content border-0 shadow animate__animated animate__fadeInUp animate__faster">
            <div className="modal-header border-bottom-0">
              <h5 className="modal-title" id="interviewActionModalLabel">
                {modalType === 'view' && "Interview Details"}
                {modalType === 'cancel' && "Cancel Interview"}
              </h5>
              {!isProcessing && (modalStatus === 'confirm' || modalStatus === 'before-delete') && (
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleModalClose}
                  aria-label="Close"
                ></button>
              )}
            </div>
            <div className="modal-body">
              {renderModalContent()}
            </div>
            {modalType === 'view' && (
              <div className="modal-footer border-top-0">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleModalClose}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    setScheduleModal(true);
                    handleModalClose();
                  }}
                  disabled={selectedInterview?.status === "canceled"}
                >
                  Reschedule
                </button>
              </div>
            )}
            {modalStatus === 'before-delete' && !isProcessing && (
              <div className="modal-footer border-top-0">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleModalClose}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => processAction()}
                >
                  Yes, Cancel Interview
                </button>
              </div>
            )}
            {modalStatus === "error" && !isProcessing && (
              <div className="modal-footer border-top-0">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleModalClose}
                >
                  OK
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <InterviewScheduler
        show={scheduleModal}
        onClose={() => setScheduleModal(false)}
        rescheduleMode={true}
        interview={{
          interviewDate: selectedInterview?.date ? new Date(selectedInterview.date) : null,
          interviewTime: selectedInterview?.time,
          ...selectedInterview
        }}
        candidate={{name: selectedInterview?.name, userid: selectedInterview?.userid}}
      />

      {showModal && (
        <div className="modal-backdrop fade show"></div>
      )}
    </>
  );
};


const ManageInterviews = () => {
  return (
    <>
      {/* <div className="site-breadcrumb">
        <div className="container">
          <h2 className="breadcrumb-title">Manage Jobs</h2>
          <ul className="breadcrumb-menu">
            <li>
              <a href="index.html">Home</a>
            </li>
            <li className="active">Manage Jobs</li>
          </ul>
        </div>
        <div className="hero-shape">
          <img 
          loading="lazy"
          role="presentation"
          decoding="async"
          fetchpriority="high"className="hero-shape-1" src="assets/img/shape/03.svg" alt=""/>
          <img 
          loading="lazy"
          role="presentation"
          decoding="async"
          fetchpriority="high"className="hero-shape-2" src="assets/img/shape/05.svg" alt=""/>
          <img 
          loading="lazy"
          role="presentation"
          decoding="async"
          fetchpriority="high"className="hero-shape-3" src="assets/img/shape/06.svg" alt=""/>
          <img 
          loading="lazy"
          role="presentation"
          decoding="async"
          fetchpriority="high"className="hero-shape-4" src="assets/img/shape/07.svg" alt=""/>
        </div>
      </div> */}
      <Breadcrumb
        title="Manage Jobs"
        path={[
          { label: "Home", href: "/", active: false },
          { label: "Manage Jobs", active: true }
        ]}
      />
      <div className='user-profile py-120'>
        <div className='container'>
          <div className='row'>
            <EmployerDashboardNav/>
            <PostedInterviews/>
          </div>
        </div>
      </div>
    </>
  );
};

export default ManageInterviews;